/**
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║  The platform theme. Edit HERE — and only here.                          ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 *
 * This file is the single source of the look: colors, surfaces, font, border
 * radius, layout and product name. Everything else (`@core/theme/*`,
 * `themeConfig.ts`, Tailwind and the `--mui-palette-*` CSS variables) is
 * derived from it — no color is written down twice.
 *
 * Save the file and you are done: Vite restarts on its own (~1s) and the page
 * comes back with the new theme — no separate build, no cache to clear.
 *
 * Only design system fine-tuning lives outside: the type scale
 * (`@core/theme/typography.ts`), shadows (`shadows.ts`, `customShadows.ts`)
 * and each mode's auxiliary tones (`colorSchemes.ts`).
 *
 * A note for anyone changing the shape of this file: besides the app, it is
 * read by `vite.config.ts` (to write the title and the initial color into the
 * HTML). So it may only hold data and type imports — never a component, a hook
 * or any module with side effects.
 */

// Type Imports
import type { Layout, LayoutComponentPosition, LayoutComponentWidth, Mode, Skin } from '@core/types'

/**
 * A theme color. In the short form you give only the main tone and the
 * light/dark tones are derived from it (`lighten 20%` / `darken 10%`, the same
 * rule the template uses).
 *
 *   primary: '#7367F0'
 *
 * The long form hands you the control — useful when the brand already defines
 * all three tones, or when the color is too light for white text:
 *
 *   primary: { main: '#FFD54F', dark: '#C8A415', contrastText: '#2F2B3D' }
 */
export type Color = string | { main: string; light?: string; dark?: string; contrastText?: string }

/** The application background and the raised surfaces (cards, menus, dialogs). */
export type Surface = { background: string; paper: string }

export type ThemeSettings = {
  brand: { name: string; homeUrl: string }
  colors: { primary: Color; secondary: Color; success: Color; error: Color; warning: Color; info: Color }
  surfaces: { light: Surface; dark: Surface }
  /**
   * Two families: `display` carries the brand — the wordmark and the headings
   * — and `fontFamily` is everything a person actually reads. Splitting them
   * is what lets the logo be geometric without a table of certificates being
   * set in the same wide, round letters.
   */
  typography: { fontFamily: string; displayFontFamily: string }
  shape: { borderRadius: number }
  appearance: { mode: Mode; skin: Skin; semiDarkMenu: boolean }
  layout: {
    type: Layout
    padding: number
    contentWidth: LayoutComponentWidth
    compactContentWidth: number
    navbar: {
      type: LayoutComponentPosition
      /** Omit to follow `contentWidth` — the usual case. */
      contentWidth?: LayoutComponentWidth
      floating: boolean
      detached: boolean
      blur: boolean
    }
    footer: {
      type: LayoutComponentPosition
      /** Omit to follow `contentWidth` — the usual case. */
      contentWidth?: LayoutComponentWidth
      detached: boolean
    }
  }
  interaction: {
    buttonRipple: boolean
    toastPosition: 'top-right' | 'top-left' | 'bottom-left' | 'bottom-right' | 'top-center' | 'bottom-center'
  }
  userCustomizable: boolean
}

export const theme = {
  brand: {
    name: 'Dilion',
    homeUrl: '/'
  },

  /**
   * The six semantic colors. Changing `primary` already moves buttons, links,
   * the active menu item, input focus, charts and the tonal backgrounds
   * (`lightOpacity`).
   */
  colors: {
    /**
     * The brand navy is `#1D2D50` — the color of the mark itself. It is too
     * dark to be the action color: the same primary serves both schemes, and
     * on the navy sidebar a navy button disappears. So the action color is the
     * open blue of the same family, and the navy became the dark surface below
     * — where it shows on every screen, sidebar included.
     */
    primary: { main: '#3A6CC4', light: '#5E8CD9', dark: '#254E96', contrastText: '#FFFFFF' },
    secondary: '#66748F',

    /**
     * `contrastText` is what gets written *on top of* the filled color, and
     * white only survives on the darker half of the palette: over the green,
     * the amber and the cyan it lands at 2.8:1, 2.4:1 and 2.9:1 — a filled
     * warning button with a white label is a decoration, not a sentence. The
     * brand navy over those same three reads at 4.8:1, 5.7:1 and 4.8:1, so the
     * color stays exactly as designed and the label changes instead.
     *
     * The red is the one case where neither works (3.9:1 white, 3.4:1 navy), so
     * it is the one color moved — 8% darker, which white clears at 4.5:1.
     */
    success: { main: '#1DAF70', contrastText: '#1D2D50' },
    error: '#D14449',
    warning: { main: '#E8952F', contrastText: '#1D2D50' },
    info: { main: '#1CA6C9', contrastText: '#1D2D50' }
  },

  /**
   * `background` is the page body; `paper` is what floats above it. On the
   * `bordered` skin the body takes the paper color and the separation becomes
   * the border instead of the contrast between the two.
   */
  surfaces: {
    // The body was a hair away from the paper (1.08:1) and the cards floated on
    // nothing; a step further down gives every card an edge without the page
    // turning grey.
    light: { background: '#E9EFF8', paper: '#FFFFFF' },
    // The brand navy, one step down for the body and the mark's own tone for
    // what floats above it — the sidebar included.
    dark: { background: '#101B33', paper: '#1D2D50' }
  },

  /**
   * Both families have to be loaded: the packages are imported in
   * `src/main.tsx`. Switching fonts means installing the package, changing
   * that import and the name here.
   */
  typography: {
    fontFamily:
      "'Plus Jakarta Sans Variable', 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    displayFontFamily: "'Poppins', 'Plus Jakarta Sans Variable', -apple-system, BlinkMacSystemFont, sans-serif"
  },

  /** Base border radius, in px. Cards, inputs and buttons derive from it. */
  shape: {
    borderRadius: 6
  },

  appearance: {
    mode: 'light', // 'light' | 'dark' | 'system' (follows the operating system)
    skin: 'default', // 'default' (elevation by shadow) | 'bordered' (by border)
    semiDarkMenu: true // dark side menu even in light mode
  },

  layout: {
    type: 'collapsed', // 'vertical' | 'collapsed' | 'horizontal'
    padding: 24, // shared padding of header, content and footer (px)

    /**
     * `wide` fills the screen; `compact` caps the content at
     * `compactContentWidth` and centers it.
     *
     * The cap only shows on a screen wider than it: at 1440 the whole page has
     * to be above ~1700px before anything moves, because the side menu eats
     * 260. Lower the cap if you want the effect on a laptop.
     *
     * The navbar and the footer follow this value unless they say otherwise —
     * their own `contentWidth` is there for the rare page that wants a wide
     * header over compact content, not for you to keep three in sync.
     */
    contentWidth: 'wide', // 'wide' | 'compact'
    compactContentWidth: 1440, // the cap used when the width is 'compact' (px)

    navbar: {
      type: 'fixed', // 'fixed' (follows the scroll) | 'static'
      floating: false, //! ignored on the horizontal layout
      detached: true, //! ignored on the horizontal layout and with a floating navbar
      blur: true // translucent background with blur
    },
    footer: {
      type: 'static', // 'static' | 'fixed' (sticks to the bottom of the screen)
      detached: false //! ignored on the horizontal layout
    }
  },

  interaction: {
    buttonRipple: true,
    toastPosition: 'top-right' // react-toastify
  },

  /**
   * `false` (the default) — the theme is what this file says, period. Choices
   * made in the Customizer are not written to the cookie, and no visitor is
   * left stuck on an old theme after you edit this file.
   *
   * `true` — hands control back to the user: the Customizer starts persisting
   * mode, skin, layout and primary color in the cookie, which then wins over
   * the values here. Use it when customization is part of the product.
   */
  userCustomizable: false
} satisfies ThemeSettings

/**
 * Where MUI stores the chosen mode. Derived from the brand name so that two
 * products served from the same domain do not fight over the same value.
 */
export const modeStorageKey = `${theme.brand.name.toLowerCase().split(' ').join('-')}-mui-template-mode`

export default theme
